import { useRef, useState } from "react";
import {
  Slider,
  Typography,
  Grid,
  useMediaQuery,
  Stack,
  Checkbox,
  FormControlLabel
} from "@material-ui/core";
import { useTheme } from "@material-ui/core/styles";
import { formatShortNumber } from "utils/Haim/utils";
import { useTranslation } from "react-i18next";
import CurrencyInput from "components/__haim/Common/CurrencyInput";

export interface PriceRangePickerProps {
  disabled?: boolean;
  initialMinPrice?: number;
  initialMaxPrice?: number;
  rangeMax?: number;
  onRangeChange: (min: number, max: number) => void;
  /**
   * Whether the budget also admits listings with no price. Off by default. The
   * checkbox is rendered only when a handler is supplied, so a caller that has
   * nowhere to store the answer doesn't show a control that does nothing.
   */
  includeListingsWithoutPrice?: boolean;
  onIncludeListingsWithoutPriceChange?: (include: boolean) => void;
}

const defaultMinPrice = 0;
const defaultMaxPrice = 10_000_000;

/**
 * Price min/max.
 *
 * Thousand separators are live-formatted by `CurrencyInput`
 * (react-number-format with `thousandSeparator`), which is also what the
 * contact dialog's budget field uses — so both sites group digits the same way
 * and neither reinvents caret handling. Formatting is display-only: the change
 * handler strips the separators and `onRangeChange` always receives plain
 * numbers.
 */
export default function PriceRangePicker({
  disabled,
  initialMinPrice,
  initialMaxPrice,
  rangeMax,
  onRangeChange,
  includeListingsWithoutPrice,
  onIncludeListingsWithoutPriceChange
}: PriceRangePickerProps) {
  const effectiveMaxPrice = rangeMax ?? defaultMaxPrice;
  const [priceRange, setPriceRange] = useState([
    initialMinPrice ?? defaultMinPrice,
    initialMaxPrice ?? effectiveMaxPrice
  ]);
  const [internalTextFieldPriceRange, setInternalTextFieldPriceRange] = useState([
    initialMinPrice ?? defaultMinPrice,
    initialMaxPrice ?? effectiveMaxPrice
  ]);
  const [internalMaxSliderValue, setInternalMaxSliderValue] = useState(
    initialMaxPrice ? initialMaxPrice * 1.2 : effectiveMaxPrice
  );
  const [internalMinSliderValue, setInternalMinSliderValue] = useState(
    initialMinPrice ? initialMinPrice * 0.8 : defaultMinPrice
  );
  // Which text field the user touched last, so an inverted range corrects the
  // bound they did NOT just type — their edit always survives.
  const lastEditedIndex = useRef<0 | 1>(0);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const { t } = useTranslation();

  const updatePriceRange = (newPriceRange: number[]) => {
    if (newPriceRange[1] < newPriceRange[0]) {
      newPriceRange = [newPriceRange[1], newPriceRange[0]];
    }
    setPriceRange(newPriceRange);
    setInternalTextFieldPriceRange(newPriceRange);
    onRangeChange(newPriceRange[0], newPriceRange[1]);
  };

  const handleOngoingSliderChange = (event, newValue) => {
    setInternalTextFieldPriceRange(newValue);

    setPriceRange(newValue);
  };

  const handleOngoingTextInputChange = (event, index: 0 | 1) => {
    lastEditedIndex.current = index;
    // `CurrencyInput` hands back the FORMATTED string ("3,200,000"); the search
    // layer only ever sees the parsed number.
    const valueWithoutCommas = event.target.value.replace(/,/g, "");
    const value = valueWithoutCommas === "" ? 0 : Number(valueWithoutCommas);
    let newRange = [];
    if (index === 0) {
      newRange = [value, priceRange[1]];
    } else {
      newRange = [priceRange[0], value];
    }
    setInternalTextFieldPriceRange(newRange);
  };

  const handleFinalTextInputChange = () => {
    let [min, max] = internalTextFieldPriceRange;

    if (min > max) {
      // Same correction rule as the rooms/floors steppers: raising min pushes
      // max up, lowering max pulls min down. A max of 0 reads as "cleared"
      // rather than a real ceiling below min, so it snaps up instead of
      // wiping the min the user already set.
      if (lastEditedIndex.current === 0 || max === 0) max = min;
      else min = max;
    }

    const newPriceRange = [min, max];
    updatePriceRange(newPriceRange);
    updateSliderMinMaxValues(newPriceRange);
  };

  const updateSliderMinMaxValues = (newPriceRange: number[]) => {
    let newMin = newPriceRange[0] * 0.8;
    newMin = newMin - (newMin % 10000);
    let newMax = newPriceRange[1] * 1.2;
    newMax = newMax - (newMax % 10000);
    setInternalMinSliderValue(newMin);
    setInternalMaxSliderValue(newMax);
  };

  // A number is LTR content even on an RTL page; `inputMode` gets phones a
  // numeric keypad instead of the Hebrew alphabet.
  const numericInputProps = { dir: "ltr", inputMode: "numeric" as const };

  return (
    <Stack sx={{ my: 2 }} alignItems="center" gap={2}>
      <Grid container spacing={2} justifyContent="center" alignItems="center">
        <Grid item xs={12} sm={5}>
          <CurrencyInput
            fullWidth
            name={t("listingsMatch.priceRangePicker.minPrice")}
            label={t("listingsMatch.priceRangePicker.minPrice")}
            variant="outlined"
            value={internalTextFieldPriceRange[0]}
            disabled={disabled}
            InputProps={{
              inputProps: { min: 0, max: internalTextFieldPriceRange[1], ...numericInputProps }
            }}
            onFocus={(event) => {
              event.target.select();
            }}
            onChange={(e) => handleOngoingTextInputChange(e, 0)}
            onBlur={() => handleFinalTextInputChange()}
          />
        </Grid>
        {!isMobile && (
          <Grid item xs={1}>
            <Typography align="center">-</Typography>
          </Grid>
        )}
        <Grid item xs={12} sm={5}>
          <CurrencyInput
            fullWidth
            name={t("listingsMatch.priceRangePicker.maxPrice")}
            label={t("listingsMatch.priceRangePicker.maxPrice")}
            variant="outlined"
            value={internalTextFieldPriceRange[1]}
            disabled={disabled}
            InputProps={{
              inputProps: { min: internalTextFieldPriceRange[0], ...numericInputProps }
            }}
            onFocus={(event) => {
              event.target.select();
            }}
            onChange={(e) => handleOngoingTextInputChange(e, 1)}
            onBlur={() => handleFinalTextInputChange()}
          />
        </Grid>
      </Grid>
      <Slider
        sx={{ maxWidth: 320 }}
        value={priceRange}
        disabled={disabled}
        onChange={handleOngoingSliderChange}
        onChangeCommitted={() => {
          updateSliderMinMaxValues(priceRange);
          updatePriceRange(priceRange);
        }}
        valueLabelDisplay="auto"
        valueLabelFormat={(v, i) => formatShortNumber(v)}
        step={10000}
        min={internalMinSliderValue}
        max={internalMaxSliderValue}
        {...(priceRange[0] &&
          priceRange[1] && {
            // A collapsed range (min === max, which the inversion correction can
            // legitimately produce) must emit ONE mark — two marks on the same
            // value collide on MUI's value-derived key.
            marks: Array.from(new Set([priceRange[0], priceRange[1]])).map((value) => ({
              value,
              label: `${formatShortNumber(value)}`
            }))
          })}
      />
      {/* A budget excludes listings with no price at all (Yad2 publishes those as
          price 0 — "מחיר לא צוין"), which is what a realtor filtering by budget
          means. This is the opt-in that brings them back ALONGSIDE the range,
          for the realtor who would rather see them and ask. */}
      {onIncludeListingsWithoutPriceChange && (
        <FormControlLabel
          sx={{ alignSelf: "flex-start", mx: 0 }}
          control={
            <Checkbox
              size="small"
              disabled={disabled}
              checked={Boolean(includeListingsWithoutPrice)}
              onChange={(event) => onIncludeListingsWithoutPriceChange(event.target.checked)}
            />
          }
          label={
            <Typography variant="body2">
              {t("listingsMatch.priceRangePicker.includeListingsWithoutPrice")}
            </Typography>
          }
        />
      )}
    </Stack>
  );
}
